import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';

import type { Action, ActionListResponse } from './types';

import { sortActions } from '~/components/Claim/Actions/ActionsHelper';
import { get } from '~/utils/request';

interface ActionsContextType {
  actions: Action[];
  isActionsLoading: boolean;
  actionsError: string | null;
  fetchActions: (claimNumber: string) => Promise<void>;
}

const ActionsContext = createContext<ActionsContextType | undefined>(undefined);

export const useActionsContext = () => {
  const ctx = useContext(ActionsContext);
  if (!ctx) throw new Error('useActionsContext must be used within an ActionsProvider');
  return ctx;
};

interface ActionsProviderProps {
  children: ReactNode;
}

export const ActionsProvider: React.FC<ActionsProviderProps> = ({ children }) => {
  const [actions, setActions] = useState<Action[]>([]);
  const [isActionsLoading, setIsActionsLoading] = useState(false);
  const [actionsError, setActionsError] = useState<string | null>(null);

  const fetchActions = useCallback(async (claimNumber: string) => {
    setIsActionsLoading(true);
    setActionsError(null);
    try {
      const response = await get<ActionListResponse>({
        url: `/api/v1/actions/${claimNumber}`,
      });
      const sortedActions = sortActions(response.actions);
      setActions(sortedActions);
    } catch (error) {
      setActionsError('Failed to fetch claim actions. please try to refresh the page.');
      console.error('Error fetching actions:', error);
    } finally {
      setIsActionsLoading(false);
    }
  }, []);

  return (
    <ActionsContext.Provider value={{ actions, isActionsLoading, actionsError, fetchActions }}>
      {children}
    </ActionsContext.Provider>
  );
};
